/**
 * Icon path data.
 *
 * Split out from Icon.tsx so that file exports a component and nothing else —
 * React Fast Refresh cannot hot-swap a module that mixes components with
 * constants, and losing HMR on the icon set means losing it on every screen.
 */

export type IconName =
  | 'search'
  | 'cart'
  | 'bell'
  | 'home'
  | 'orders'
  | 'user'
  | 'chevron'
  | 'back'
  | 'down'
  | 'up'
  | 'check'
  | 'x'
  | 'plus'
  | 'minus'
  | 'filter'
  | 'sort'
  | 'heart'
  | 'paw'
  | 'pin'
  | 'phone'
  | 'message'
  | 'truck'
  | 'box'
  | 'scan'
  | 'shield'
  | 'alert'
  | 'chart'
  | 'grid'
  | 'money'
  | 'store'
  | 'pill'
  | 'settings'
  | 'doc'
  | 'users'
  | 'wifi-off'
  | 'refresh'
  | 'star'
  | 'camera'
  | 'arrow-up'
  | 'clock'
  | 'flag'
  | 'trash'
  | 'eye'
  // ── The writing toolbar ──────────────────────────────────────────────────
  // Drawn rather than imported: pulling in an icon package for eleven glyphs
  // would ship a few hundred of them to every buyer who loads the shop front,
  // because the console and the storefront are one bundle.
  | 'bold'
  | 'italic'
  | 'strikethrough'
  | 'heading'
  | 'heading-small'
  | 'list'
  | 'list-ordered'
  | 'quote'
  | 'link'
  | 'code'
  | 'image'
  // ── The social marks ─────────────────────────────────────────────────────
  //
  // Prefixed, and not `x`/`instagram`: `x` is already the close button, and a
  // name collision on an icon is a close button that renders as a logo.
  //
  // These five and no others, because these five are the ones Settings offers
  // (SettingsRegistry: social.instagram, .facebook, .x, .linkedin, .tiktok).
  // A sixth glyph nothing can select is dead weight in every buyer's bundle.
  | 'brand-instagram'
  | 'brand-facebook'
  | 'brand-x'
  | 'brand-linkedin'
  | 'brand-tiktok';

export const iconPaths: Record<IconName, React.ReactNode> = {
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </>
  ),
  cart: (
    <>
      <path d="M3 4h2l2.5 11h10L20 7H6" />
      <circle cx="9" cy="19" r="1.4" />
      <circle cx="17" cy="19" r="1.4" />
    </>
  ),
  bell: (
    <>
      <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9z" />
      <path d="M10 18a2 2 0 0 0 4 0" />
    </>
  ),
  home: <path d="M4 11l8-6 8 6v8a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1z" />,
  orders: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h8M8 13h8M8 17h5" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" />
    </>
  ),
  chevron: <path d="M9 5l7 7-7 7" />,
  back: <path d="M15 5l-7 7 7 7" />,
  down: <path d="M5 9l7 7 7-7" />,
  up: <path d="M5 15l7-7 7 7" />,
  check: <path d="M4 12.5l5.5 5.5L20 7" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  filter: <path d="M4 6h16M7 12h10M10 18h4" />,
  sort: <path d="M7 5v14M7 19l-3-3M17 19V5M17 5l3 3" />,
  heart: <path d="M12 20s-7-4.5-7-9.5A4 4 0 0 1 12 8a4 4 0 0 1 7 2.5c0 5-7 9.5-7 9.5z" />,
  pin: (
    <>
      <path d="M12 21s6-5.5 6-10a6 6 0 1 0-12 0c0 4.5 6 10 6 10z" />
      <circle cx="12" cy="11" r="2.2" />
    </>
  ),
  phone: <path d="M6 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5L15 12l4 1.5V17a2 2 0 0 1-2.2 2A15 15 0 0 1 4 6.2 2 2 0 0 1 6 4z" />,
  message: <path d="M4 5h16v11H9l-5 4z" />,
  truck: (
    <>
      <path d="M2 6h11v10H2z" />
      <path d="M13 9h4l3 3v4h-7z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
    </>
  ),
  box: (
    <>
      <path d="M12 3l8 4v10l-8 4-8-4V7z" />
      <path d="M4 7l8 4 8-4M12 11v10" />
    </>
  ),
  scan: <path d="M4 8V5h3M20 8V5h-3M4 16v3h3M20 16v3h-3M4 12h16" />,
  shield: <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />,
  alert: (
    <>
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  chart: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" />
      <rect x="13" y="4" width="7" height="7" />
      <rect x="4" y="13" width="7" height="7" />
      <rect x="13" y="13" width="7" height="7" />
    </>
  ),
  money: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  store: (
    <>
      <path d="M4 9h16v10H4z" />
      <path d="M4 9l1.5-5h13L20 9M9 19v-6h6v6" />
    </>
  ),
  pill: (
    <>
      <rect x="3" y="8" width="18" height="8" rx="4" />
      <path d="M12 8v8" />
    </>
  ),
  /* The animal-health mark.
     THREE toes and a pad, not four. It is drawn at 14px on the shop switch and
     nowhere larger, and a fourth toe at that size closes up into a smudge —
     the shape stops reading as a paw and starts reading as a blot. Three
     bigger circles survive the resolution, which is the only thing this icon
     has to do. */
  paw: (
    <>
      <circle cx="7" cy="10" r="2.2" />
      <circle cx="12" cy="8" r="2.2" />
      <circle cx="17" cy="10" r="2.2" />
      <path d="M12 12.6c2.6 0 5 2 5 4.2 0 1.6-1.4 2.6-3 2.3-.7-.1-1.3-.3-2-.3s-1.3.2-2 .3c-1.6.3-3-.7-3-2.3 0-2.2 2.4-4.2 5-4.2Z" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.5 5.5l2 2M16.5 16.5l2 2M18.5 5.5l-2 2M7.5 16.5l-2 2" />
    </>
  ),
  doc: (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4M9 13h6M9 17h4" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5" />
      <path d="M16 6a3 3 0 0 1 0 6M17 19c0-2-.6-3.5-1.6-4.6" />
    </>
  ),
  'wifi-off': (
    <>
      <path d="M3 8a16 16 0 0 1 18 0M6.5 12a11 11 0 0 1 11 0M10 15.5a6 6 0 0 1 4 0" />
      <path d="M12 19h.01" />
      <path d="M3 3l18 18" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 12a8 8 0 1 1-2.3-5.7" />
      <path d="M20 4v5h-5" />
    </>
  ),
  star: <path d="M12 4l2.4 5 5.6.7-4 3.9 1 5.4-5-2.7-5 2.7 1-5.4-4-3.9 5.6-.7z" />,
  camera: (
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3v11H4z" />
      <circle cx="12" cy="13" r="3.2" />
    </>
  ),
  'arrow-up': <path d="M12 20V6M6 12l6-6 6 6" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4.5l3 1.8" />
    </>
  ),
  flag: <path d="M6 21V4h12l-2.5 4L18 12H6" />,
  // Removing the last of something from a cart is a delete, not a dismiss —
  // an "x" there reads as "close" and buyers hesitate over it.
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),

  // ── The writing toolbar ──────────────────────────────────────────────────

  bold: (
    <>
      <path d="M7 5h5.5a3.5 3.5 0 0 1 0 7H7z" />
      <path d="M7 12h6.5a3.5 3.5 0 0 1 0 7H7z" />
    </>
  ),
  italic: (
    <>
      <path d="M15 5h-5M14 19H9M14 5l-4 14" />
    </>
  ),
  strikethrough: (
    <>
      <path d="M16 6.5A4 4 0 0 0 12 5c-2.2 0-4 1.2-4 3s1.6 2.6 4 3.3" />
      <path d="M8.5 17a4.2 4.2 0 0 0 3.8 2c2.4 0 4.2-1.2 4.2-3.1 0-1.3-.8-2.2-2.3-2.8" />
      <path d="M4 12h16" />
    </>
  ),
  // A big H and a small one. The alternative is "H2" and "H3" as text, which
  // needs a font inside an SVG and does not scale with the icon size.
  heading: (
    <>
      <path d="M6 5v14M16 5v14M6 12h10" />
    </>
  ),
  'heading-small': (
    <>
      <path d="M7 8v11M15 8v11M7 13.5h8" />
    </>
  ),
  list: (
    <>
      <path d="M9 7h11M9 12h11M9 17h11" />
      <circle cx="5" cy="7" r="1.1" />
      <circle cx="5" cy="12" r="1.1" />
      <circle cx="5" cy="17" r="1.1" />
    </>
  ),
  'list-ordered': (
    <>
      <path d="M10 7h10M10 12h10M10 17h10" />
      <path d="M4 6h1.5v3M4 11.2h1.6L4 13.8h1.7M4 16.2h1.6v1.3H4.2v1.3h1.4" />
    </>
  ),
  quote: (
    <>
      <path d="M4 5v14" />
      <path d="M9 8h11M9 12h11M9 16h7" />
    </>
  ),
  link: (
    <>
      <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.3-2.3a4 4 0 0 0-5.7-5.7l-1.3 1.3" />
      <path d="M13.5 10.5a4 4 0 0 0-5.7 0l-2.3 2.3a4 4 0 0 0 5.7 5.7l1.3-1.3" />
    </>
  ),
  code: (
    <>
      <path d="M9 7l-5 5 5 5M15 7l5 5-5 5" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <circle cx="8.5" cy="10" r="1.6" />
      <path d="M3.5 17l5-4.5 4 3.5 3-2.5 5 4" />
    </>
  ),

  // ══════════════════════════════════════════════════════════════════════════
  //  THE SOCIAL MARKS
  // ══════════════════════════════════════════════════════════════════════════
  //
  // ── WHY THESE ARE FILLED AND EVERY OTHER ICON IS NOT ────────────────────
  //
  // The rest of this set is a drawing language: one weight, no fill, so a row
  // of glyphs sits at the same optical weight as Satoshi body text. A brand
  // mark is not part of that language — it is a specific shape people
  // recognise, and the recognition is the entire reason it is there.
  //
  // Outlined versions were tried first. At 13px an outlined Facebook "f"
  // inside an outlined rounded square is four grey strokes that could be
  // anything, and an outlined TikTok note is unreadable. So these are the real
  // marks, rendered with `filled`, and they are the one documented exception.
  //
  // ── AND WHY THEY ARE DRAWN RATHER THAN IMPORTED ─────────────────────────
  //
  // Same reason as the writing toolbar above: the console and the storefront
  // are one bundle, so an icon package for five glyphs ships a few hundred of
  // them to every buyer who loads the shop front.
  'brand-instagram': (
    <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.42.56.21.96.47 1.38.9.42.42.68.82.9 1.38.16.42.36 1.05.41 2.22.06 1.27.07 1.65.07 4.85s-.01 3.59-.07 4.85c-.06 1.17-.26 1.81-.42 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.07.36-2.24.41-1.27.06-1.65.07-4.86.07s-3.59-.01-4.86-.07c-1.17-.06-1.82-.26-2.24-.42-.57-.22-.96-.48-1.38-.9-.42-.42-.69-.82-.9-1.38-.17-.42-.36-1.07-.42-2.24-.05-1.26-.06-1.65-.06-4.84s.02-3.59.06-4.86c.06-1.17.26-1.81.42-2.23.21-.57.48-.96.9-1.38.42-.42.81-.69 1.38-.9.42-.17 1.05-.36 2.22-.42 1.28-.05 1.65-.06 4.86-.06ZM12 0C8.74 0 8.33.01 7.05.07c-1.28.06-2.15.26-2.91.56-.79.31-1.46.72-2.13 1.38C1.35 2.68.94 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.28.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.67 1.34 1.08 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.28-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.67-.67 1.08-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.28-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13C21.32 1.35 20.65.94 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32ZM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm7.85-10.4a1.44 1.44 0 1 1-2.88 0 1.44 1.44 0 0 1 2.88 0Z" />
  ),
  'brand-facebook': (
    <path d="M9.1 23.69v-7.98H6.63v-3.67H9.1v-1.58c0-4.08 1.85-5.98 5.86-5.98.4 0 .95.04 1.47.1.51.06.9.13 1.14.2v3.32a8.6 8.6 0 0 0-.65-.04 26.8 26.8 0 0 0-.74-.01c-.7 0-1.25.1-1.67.31a1.69 1.69 0 0 0-.68.62c-.26.42-.37 1-.37 1.75v1.3h3.92l-.39 2.1-.29 1.57h-3.24v8.24C19.4 23.24 24 18.18 24 12.04 24 5.42 18.63.05 12 .05S0 5.42 0 12.04c0 5.63 3.87 10.35 9.1 11.65Z" />
  ),
  'brand-x': (
    <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.59-6.63 7.59H.47l8.6-9.83L0 1.15h7.6l5.24 6.94 6.06-6.94Zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.4Z" />
  ),
  'brand-linkedin': (
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.04c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13Zm1.78 13.02H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45C23.2 24 24 23.23 24 22.27V1.73C24 .77 23.2 0 22.22 0Z" />
  ),
  'brand-tiktok': (
    <path d="M12.53.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07Z" />
  ),
};

export const iconNames = Object.keys(iconPaths) as IconName[];
